"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useMemo } from "react";
import { useTranslations } from "next-intl";
import { useNetlifyForm, netlifyFormMessage } from "@/components/use-netlify-form";
import { FormStatus } from "@/components/form-status";
import { DatePickerField } from "@/components/ui/date-picker";
import { TimePickerField } from "@/components/ui/time-picker";
import rentalDataEn from "@/content/mtb-rental-packs-summer-2025.json";
import rentalDataFr from "@/content/mtb-rental-packs-summer-2025.fr.json";
import rentalDataNl from "@/content/mtb-rental-packs-summer-2025.nl.json";
import rentalDataEs from "@/content/mtb-rental-packs-summer-2025.es.json";
import rentalDataIt from "@/content/mtb-rental-packs-summer-2025.it.json";
import Image from "next/image";

// Get locale-specific rental data
const getRentalData = (locale: string): typeof rentalDataEn => {
  switch (locale) {
    case 'fr':
      return rentalDataFr as typeof rentalDataEn;
    case 'nl':
      return rentalDataNl as typeof rentalDataEn;
    case 'es':
      return rentalDataEs as typeof rentalDataEn;
    case 'it':
      return rentalDataIt as typeof rentalDataEn;
    default:
      return rentalDataEn;
  }
};
interface ContactFormProps {
  locale: string;
}
interface RentalItem {
  packId: string;
  category: "adult" | "junior" | "other";
  quantity: number;
  duration: "2h" | "halfday" | "1day";
  withInsurance: boolean;
  accessorySize?: "adult" | "junior"; // For accessories that need size specification
  itemType?: "complete" | "bikeOnly" | "helmetOnly"; // For separate pricing
  selectedProducts?: string[]; // Selected product IDs from the pack
}
export default function ContactFormSummerRental(t0) {
  const $ = _c(213);
  const {
    locale
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [rentalItems, setRentalItems] = useState(t1);
  const [totalPrice, setTotalPrice] = useState(0);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [carouselIndexes, setCarouselIndexes] = useState(t2);
  const t = useTranslations("ContactSummer");
  const tf = useTranslations("ContactForm");
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setRentalItems([]);
      setTotalPrice(0);
      setCarouselIndexes({});
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    status: netlifyStatus,
    handleSubmit: handleNetlifySubmit
  } = useNetlifyForm(t3);
  const today = new Date().toISOString().split("T")[0];
  let t4;
  if ($[3] !== locale) {
    t4 = getRentalData(locale);
    $[3] = locale;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const rentalData = t4;
  let t5;
  if ($[5] !== rentalData.adultPacks || $[6] !== rentalData.juniorPacks || $[7] !== rentalData.otherRentals) {
    t5 = [...rentalData.adultPacks.map(_temp), ...rentalData.juniorPacks.map(_temp2), ...rentalData.otherRentals.map(_temp3)];
    $[5] = rentalData.adultPacks;
    $[6] = rentalData.juniorPacks;
    $[7] = rentalData.otherRentals;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const allPacks = t5;
  let t6;
  let t7;
  if ($[9] !== allPacks || $[10] !== rentalData.damageInsurance || $[11] !== rentalItems) {
    t6 = () => {
      let total = 0;
      rentalItems.forEach(item => {
        const pack = allPacks.find(p_2 => p_2.id === item.packId && p_2.category === item.category);
        if (pack) {
          let itemPrice = 0;
          if (item.category === "other" && "standalonePricing" in pack && pack.standalonePricing) {
            itemPrice = pack.standalonePricing[item.duration] || 0;
          } else {
            if ("pricing" in pack) {
              itemPrice = pack.pricing[item.duration] || 0;
            }
          }
          total = total + itemPrice * item.quantity;
          total;
          if (item.withInsurance && rentalData.damageInsurance) {
            total = total + rentalData.damageInsurance.price * item.quantity;
            total;
          }
        }
      });
      setTotalPrice(total);
    };
    t7 = [rentalItems, allPacks, rentalData.damageInsurance];
    $[9] = allPacks;
    $[10] = rentalData.damageInsurance;
    $[11] = rentalItems;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] !== rentalItems) {
    t8 = index => {
      setRentalItems(rentalItems.filter((_, i) => i !== index));
    };
    $[14] = rentalItems;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const removeRentalItem = t8;
  let t9;
  if ($[16] !== allPacks || $[17] !== rentalItems) {
    t9 = (index_0, field, value) => {
      setRentalItems(rentalItems.map((item_0, i_0) => {
        if (i_0 === index_0) {
          if (field === "category") {
            const defaultPack = allPacks.find(p_3 => p_3.category === value);
            return {
              ...item_0,
              category: value as RentalItem["category"],
              packId: defaultPack?.id || item_0.packId
            };
          }
          return {
            ...item_0,
            [field]: value
          } as RentalItem;
        }
        return item_0;
      }));
    };
    $[16] = allPacks;
    $[17] = rentalItems;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const updateRentalItem = t9;
  let t10;
  if ($[19] !== allPacks) {
    t10 = (packId, category) => {
      const pack_0 = allPacks.find(p_4 => p_4.id === packId && p_4.category === category);
      return pack_0?.name || "";
    };
    $[19] = allPacks;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const getPackName = t10;
  let t11;
  if ($[21] !== allPacks) {
    t11 = item_1 => {
      const pack_1 = allPacks.find(p_5 => p_5.id === item_1.packId && p_5.category === item_1.category);
      if (!pack_1) {
        return 0;
      }
      if (item_1.itemType && item_1.itemType !== "complete" && "separateItems" in pack_1 && pack_1.separateItems) {
        const separateItems = pack_1.separateItems as Record<string, Record<string, number>>;
        if (item_1.itemType === "bikeOnly" && separateItems.bikeOnly) {
          return separateItems.bikeOnly[item_1.duration] || 0;
        }
        if (item_1.itemType === "helmetOnly" && separateItems.helmetOnly) {
          return separateItems.helmetOnly[item_1.duration] || 0;
        }
      }
      if (item_1.category === "other" && "standalonePricing" in pack_1 && pack_1.standalonePricing) {
        return pack_1.standalonePricing[item_1.duration] || 0;
      } else {
        if ("pricing" in pack_1) {
          return pack_1.pricing[item_1.duration] || 0;
        }
      }
      return 0;
    };
    $[21] = allPacks;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const getPackPrice = t11;
  let t12;
  if ($[23] !== allPacks) {
    t12 = (packId_0, category_0) => {
      const pack_2 = allPacks.find(p_6 => p_6.id === packId_0 && p_6.category === category_0);
      return pack_2 && "products" in pack_2 ? pack_2.products : [];
    };
    $[23] = allPacks;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const getPackProducts = t12;
  let t13;
  if ($[25] !== tf) {
    t13 = d => {
      if (d === "2h") {
        return "2h";
      }
      if (d === "4h") {
        return "4h";
      }
      if (d === "halfday") {
        return tf("halfDay");
      }
      const n = parseInt(d, 10);
      if (!Number.isNaN(n)) {
        return `${n} ${n === 1 ? tf("day") : tf("days")}`;
      }
      return d;
    };
    $[25] = tf;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const durationLabel = t13;
  let t14;
  if ($[27] !== allPacks || $[28] !== getPackPrice || $[29] !== rentalData.damageInsurance || $[30] !== rentalItems) {
    t14 = () => {
      const itemsData = rentalItems.map((item_2, index_1) => {
        const pack_3 = allPacks.find(p_7 => p_7.id === item_2.packId && p_7.category === item_2.category);
        const packName = pack_3?.name || "Unknown";
        const price = getPackPrice(item_2);
        const insurance = item_2.withInsurance ? rentalData.damageInsurance.price : 0;
        let itemInfo = `Rider ${index_1 + 1}: ${packName}\n` + `Category: ${item_2.category}\n`;
        if (item_2.itemType && item_2.itemType !== "complete") {
          const typeLabels = {
            bikeOnly: "Bike only",
            helmetOnly: "Helmet only"
          };
          itemInfo = itemInfo + `Type: ${typeLabels[item_2.itemType] || item_2.itemType}\n`;
        }
        if (item_2.category === "other" && item_2.accessorySize) {
          itemInfo = itemInfo + `Size: ${item_2.accessorySize}\n`;
        }
        if (item_2.selectedProducts && item_2.selectedProducts.length > 0) {
          itemInfo = itemInfo + `Selected products: ${item_2.selectedProducts.join(", ")}\n`;
        }
        itemInfo = itemInfo + (`Quantity: ${item_2.quantity}\n` + `Duration: ${item_2.duration}\n` + `Price per unit: ${price}€\n` + `Insurance: ${item_2.withInsurance ? "Yes (+" + insurance + "\u20AC/unit)" : "No"}\n` + `Subtotal: ${(price + insurance) * item_2.quantity}€`);
        return itemInfo;
      }).join("\n\n");
      return itemsData;
    };
    $[27] = allPacks;
    $[28] = getPackPrice;
    $[29] = rentalData.damageInsurance;
    $[30] = rentalItems;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const prepareFormData = t14;
  let t15;
  let t16;
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <input type="hidden" name="form-name" value="contact-summer-rental" />;
    t16 = <input type="hidden" name="formType" value="rental" />;
    t17 = <input type="hidden" name="season" value="summer" />;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
  } else {
    t15 = $[32];
    t16 = $[33];
    t17 = $[34];
  }
  let t18;
  if ($[35] !== prepareFormData) {
    t18 = prepareFormData();
    $[35] = prepareFormData;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t18) {
    t19 = <input type="hidden" name="rentalData" value={t18} />;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const t20 = `${totalPrice}€`;
  let t21;
  if ($[39] !== t20) {
    t21 = <input type="hidden" name="totalPrice" value={t20} />;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <p className="hidden"><label>Don't fill this out if you're human:{" "}<input name="bot-field" /></label></p>;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== tf) {
    t23 = tf("mtbEquipmentRental");
    $[42] = tf;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t23) {
    t24 = <h2 className="text-2xl md:text-3xl font-bold text-foreground">{t23}</h2>;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== tf) {
    t25 = tf("mtbRentalDescription");
    $[46] = tf;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== t25) {
    t26 = <p className="mt-1 text-muted-foreground">{t25}</p>;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t24 || $[51] !== t26) {
    t27 = <div className="mb-10 md:mb-12 text-center">{t24}{t26}</div>;
    $[50] = t24;
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t) {
    t28 = t("firstName");
    $[53] = t;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== t28) {
    t29 = <label htmlFor="firstName" className="block text-base font-medium mb-2">{t28} *</label>;
    $[55] = t28;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <input type="text" id="firstName" name="firstName" required={true} className="w-full px-4 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background" />;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  let t31;
  if ($[58] !== t29) {
    t31 = <div>{t29}{t30}</div>;
    $[58] = t29;
    $[59] = t31;
  } else {
    t31 = $[59];
  }
  let t32;
  if ($[60] !== t) {
    t32 = t("lastName");
    $[60] = t;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  let t33;
  if ($[62] !== t32) {
    t33 = <label htmlFor="lastName" className="block text-base font-medium mb-2">{t32} *</label>;
    $[62] = t32;
    $[63] = t33;
  } else {
    t33 = $[63];
  }
  let t34;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <input type="text" id="lastName" name="lastName" required={true} className="w-full px-4 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background" />;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  let t35;
  if ($[65] !== t33) {
    t35 = <div>{t33}{t34}</div>;
    $[65] = t33;
    $[66] = t35;
  } else {
    t35 = $[66];
  }
  let t36;
  if ($[67] !== t) {
    t36 = t("email");
    $[67] = t;
    $[68] = t36;
  } else {
    t36 = $[68];
  }
  let t37;
  if ($[69] !== t36) {
    t37 = <label htmlFor="email" className="block text-base font-medium mb-2">{t36} *</label>;
    $[69] = t36;
    $[70] = t37;
  } else {
    t37 = $[70];
  }
  let t38;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <input type="email" id="email" name="email" required={true} className="w-full px-4 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background" />;
    $[71] = t38;
  } else {
    t38 = $[71];
  }
  let t39;
  if ($[72] !== t37) {
    t39 = <div>{t37}{t38}</div>;
    $[72] = t37;
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  let t40;
  if ($[74] !== t) {
    t40 = t("phone");
    $[74] = t;
    $[75] = t40;
  } else {
    t40 = $[75];
  }
  let t41;
  if ($[76] !== t40) {
    t41 = <label htmlFor="phone" className="block text-base font-medium mb-2">{t40}</label>;
    $[76] = t40;
    $[77] = t41;
  } else {
    t41 = $[77];
  }
  let t42;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <input type="tel" id="phone" name="phone" className="w-full px-4 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background" />;
    $[78] = t42;
  } else {
    t42 = $[78];
  }
  let t43;
  if ($[79] !== t41) {
    t43 = <div>{t41}{t42}</div>;
    $[79] = t41;
    $[80] = t43;
  } else {
    t43 = $[80];
  }
  let t44;
  if ($[81] !== tf) {
    t44 = tf("pickupDate");
    $[81] = tf;
    $[82] = t44;
  } else {
    t44 = $[82];
  }
  let t45;
  if ($[83] !== t44) {
    t45 = <label htmlFor="pickupDate" className="text-base font-medium">{t44} *</label>;
    $[83] = t44;
    $[84] = t45;
  } else {
    t45 = $[84];
  }
  let t46;
  if ($[85] !== tf) {
    t46 = tf("dateExample");
    $[85] = tf;
    $[86] = t46;
  } else {
    t46 = $[86];
  }
  let t47;
  if ($[87] !== t46) {
    t47 = <span className="text-sm text-muted-foreground">{t46}</span>;
    $[87] = t46;
    $[88] = t47;
  } else {
    t47 = $[88];
  }
  let t48;
  if ($[89] !== t45 || $[90] !== t47) {
    t48 = <div className="flex items-center justify-between mb-2">{t45}{t47}</div>;
    $[89] = t45;
    $[90] = t47;
    $[91] = t48;
  } else {
    t48 = $[91];
  }
  let t49;
  if ($[92] !== tf) {
    t49 = tf("pickupTime");
    $[92] = tf;
    $[93] = t49;
  } else {
    t49 = $[93];
  }
  let t50;
  if ($[94] !== t49) {
    t50 = <label htmlFor="pickupTime" className="text-base font-medium">{t49}</label>;
    $[94] = t49;
    $[95] = t50;
  } else {
    t50 = $[95];
  }
  let t51;
  if ($[96] !== tf) {
    t51 = tf("timeExample");
    $[96] = tf;
    $[97] = t51;
  } else {
    t51 = $[97];
  }
  let t52;
  if ($[98] !== t51) {
    t52 = <span className="text-sm text-muted-foreground">{t51}</span>;
    $[98] = t51;
    $[99] = t52;
  } else {
    t52 = $[99];
  }
  let t53;
  if ($[100] !== t50 || $[101] !== t52) {
    t53 = <div className="flex items-center justify-between mb-2">{t50}{t52}</div>;
    $[100] = t50;
    $[101] = t52;
    $[102] = t53;
  } else {
    t53 = $[102];
  }
  const t54 = locale === "fr" ? "Choisir une heure" : "Pick a time";
  let t55;
  if ($[103] !== t54) {
    t55 = <TimePickerField name="pickupTime" id="pickupTime" placeholder={t54} />;
    $[103] = t54;
    $[104] = t55;
  } else {
    t55 = $[104];
  }
  let t56;
  if ($[105] !== t53 || $[106] !== t55) {
    t56 = <div>{t53}{t55}</div>;
    $[105] = t53;
    $[106] = t55;
    $[107] = t56;
  } else {
    t56 = $[107];
  }
  let t57;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center"><svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg></div>;
    $[108] = t57;
  } else {
    t57 = $[108];
  }
  let t58;
  if ($[109] !== tf) {
    t58 = tf("couponLabel");
    $[109] = tf;
    $[110] = t58;
  } else {
    t58 = $[110];
  }
  let t59;
  if ($[111] !== t58) {
    t59 = <span className="font-bold text-sm text-foreground">{t58}</span>;
    $[111] = t58;
    $[112] = t59;
  } else {
    t59 = $[112];
  }
  let t60;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <span className="font-bold text-green-600 text-sm">-20%</span>;
    $[113] = t60;
  } else {
    t60 = $[113];
  }
  let t61;
  if ($[114] !== t59) {
    t61 = <div className="flex items-center gap-2 mb-1">{t59}{t60}</div>;
    $[114] = t59;
    $[115] = t61;
  } else {
    t61 = $[115];
  }
  let t62;
  if ($[116] !== tf) {
    t62 = tf("couponLabel");
    $[116] = tf;
    $[117] = t62;
  } else {
    t62 = $[117];
  }
  let t63;
  if ($[118] !== tf) {
    t63 = tf("couponPlaceholder");
    $[118] = tf;
    $[119] = t63;
  } else {
    t63 = $[119];
  }
  let t64;
  if ($[120] !== t62 || $[121] !== t63) {
    t64 = <input type="text" id="couponCode" name="couponCode" aria-label={t62} placeholder={t63} className="w-full px-3 py-2.5 text-base border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-background" />;
    $[120] = t62;
    $[121] = t63;
    $[122] = t64;
  } else {
    t64 = $[122];
  }
  let t65;
  if ($[123] !== t61 || $[124] !== t64) {
    t65 = <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-4 shadow-sm"><div className="flex items-center gap-4">{t57}<div className="flex-1 min-w-0">{t61}{t64}</div></div></div>;
    $[123] = t61;
    $[124] = t64;
    $[125] = t65;
  } else {
    t65 = $[125];
  }
  let t66;
  if ($[126] !== tf) {
    t66 = tf("rentalEquipment");
    $[126] = tf;
    $[127] = t66;
  } else {
    t66 = $[127];
  }
  let t67;
  if ($[128] !== t66) {
    t67 = <h2 className="text-xl font-bold mb-4">{t66}</h2>;
    $[128] = t66;
    $[129] = t67;
  } else {
    t67 = $[129];
  }
  let t68;
  if ($[130] !== allPacks || $[131] !== rentalItems) {
    t68 = () => {
      const defaultPack_0 = allPacks.find(_temp4);
      if (defaultPack_0) {
        setRentalItems([...rentalItems, {
          packId: defaultPack_0.id,
          category: "adult",
          quantity: 1,
          duration: "1day",
          withInsurance: false
        }]);
      }
    };
    $[130] = allPacks;
    $[131] = rentalItems;
    $[132] = t68;
  } else {
    t68 = $[132];
  }
  let t69;
  if ($[133] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>;
    $[133] = t69;
  } else {
    t69 = $[133];
  }
  let t70;
  if ($[134] !== tf) {
    t70 = tf("addAdult");
    $[134] = tf;
    $[135] = t70;
  } else {
    t70 = $[135];
  }
  let t71;
  if ($[136] !== t68 || $[137] !== t70) {
    t71 = <button type="button" onClick={t68} className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary text-secondary-foreground rounded-lg font-semibold hover:bg-secondary/90 transition-colors text-sm">{t69}{t70}</button>;
    $[136] = t68;
    $[137] = t70;
    $[138] = t71;
  } else {
    t71 = $[138];
  }
  let t72;
  if ($[139] !== allPacks || $[140] !== rentalItems) {
    t72 = () => {
      const defaultPack_1 = allPacks.find(_temp5);
      if (defaultPack_1) {
        setRentalItems([...rentalItems, {
          packId: defaultPack_1.id,
          category: "junior",
          quantity: 1,
          duration: "1day",
          withInsurance: false
        }]);
      }
    };
    $[139] = allPacks;
    $[140] = rentalItems;
    $[141] = t72;
  } else {
    t72 = $[141];
  }
  let t73;
  if ($[142] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>;
    $[142] = t73;
  } else {
    t73 = $[142];
  }
  let t74;
  if ($[143] !== tf) {
    t74 = tf("addJunior");
    $[143] = tf;
    $[144] = t74;
  } else {
    t74 = $[144];
  }
  let t75;
  if ($[145] !== t72 || $[146] !== t74) {
    t75 = <button type="button" onClick={t72} className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary text-secondary-foreground rounded-lg font-semibold hover:bg-secondary/90 transition-colors text-sm">{t73}{t74}</button>;
    $[145] = t72;
    $[146] = t74;
    $[147] = t75;
  } else {
    t75 = $[147];
  }
  let t76;
  if ($[148] !== allPacks || $[149] !== rentalItems) {
    t76 = () => {
      const defaultPack_2 = allPacks.find(_temp6);
      if (defaultPack_2) {
        setRentalItems([...rentalItems, {
          packId: defaultPack_2.id,
          category: "other",
          quantity: 1,
          duration: "1day",
          withInsurance: false,
          accessorySize: "adult"
        }]);
      }
    };
    $[148] = allPacks;
    $[149] = rentalItems;
    $[150] = t76;
  } else {
    t76 = $[150];
  }
  let t77;
  if ($[151] === Symbol.for("react.memo_cache_sentinel")) {
    t77 = <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>;
    $[151] = t77;
  } else {
    t77 = $[151];
  }
  let t78;
  if ($[152] !== tf) {
    t78 = tf("addAccessory");
    $[152] = tf;
    $[153] = t78;
  } else {
    t78 = $[153];
  }
  let t79;
  if ($[154] !== t76 || $[155] !== t78) {
    t79 = <button type="button" onClick={t76} className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent/90 transition-colors text-sm">{t77}{t78}</button>;
    $[154] = t76;
    $[155] = t78;
    $[156] = t79;
  } else {
    t79 = $[156];
  }
  let t80;
  if ($[157] !== t71 || $[158] !== t75 || $[159] !== t79) {
    t80 = <div className="flex flex-wrap gap-2 mb-4">{t71}{t75}{t79}</div>;
    $[157] = t71;
    $[158] = t75;
    $[159] = t79;
    $[160] = t80;
  } else {
    t80 = $[160];
  }
  let t81;
  if ($[161] !== rentalItems.length || $[162] !== tf) {
    t81 = rentalItems.length === 0 && <div className="p-8 text-center bg-muted/30 rounded-lg"><p className="text-muted-foreground">{tf("emptyRentalMessage")}</p></div>;
    $[161] = rentalItems.length;
    $[162] = tf;
    $[163] = t81;
  } else {
    t81 = $[163];
  }
  let t82;
  if ($[164] !== allPacks || $[165] !== carouselIndexes || $[166] !== getPackProducts || $[167] !== removeRentalItem || $[168] !== rentalData.damageInsurance || $[169] !== rentalItems || $[170] !== tf || $[171] !== updateRentalItem) {
    t82 = rentalItems.map((item_3, index_2) => {
      const pack_4 = allPacks.find(p_11 => p_11.id === item_3.packId && p_11.category === item_3.category);
      const products = getPackProducts(item_3.packId, item_3.category);
      return <div key={index_2} className="mb-6 p-5 bg-card rounded-xl relative border border-border shadow-sm"><button type="button" onClick={() => removeRentalItem(index_2)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-muted hover:bg-destructive/20 flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors" aria-label="Remove item">✕</button><div className="flex items-center gap-3 mb-4 pb-3 border-b"><div className="w-8 h-8 rounded-full bg-[url('/leather.jpg')] bg-cover bg-center border border-[#2a1f10]/50 flex items-center justify-center text-white"><svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18.5" cy="17.5" r="3.5" /><circle cx="5.5" cy="17.5" r="3.5" /><circle cx="15" cy="5" r="1" /><path d="M12 17.5V14l-3-3 4-3 2 3h2" /></svg></div><h4 className="font-bold text-lg">{item_3.category === "other" ? tf("accessories") : `${tf("cyclist")} ${rentalItems.slice(0, index_2 + 1).filter(_temp7).length}`}</h4></div><div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4"><div><label htmlFor={`category-${index_2}`} className="block text-base font-medium mb-2">{tf("category")}</label><select id={`category-${index_2}`} value={item_3.category} onChange={e => updateRentalItem(index_2, "category", e.target.value)} className="w-full pl-4 pr-10 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3cpath%20fill%3D%22%236b7280%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2000-1.414-1.414z%22%2F%3E%3c%2Fsvg%3E')] bg-[length:12px] bg-[right_12px_center] bg-no-repeat"><option value="adult">{tf("adult")}</option><option value="junior">{tf("junior")}</option><option value="other">{tf("accessories")}</option></select></div><div><label htmlFor={`pack-${index_2}`} className="block text-base font-medium mb-2">{tf("packItem")}</label><select id={`pack-${index_2}`} value={item_3.packId} onChange={e_0 => updateRentalItem(index_2, "packId", e_0.target.value)} className="w-full pl-4 pr-10 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3cpath%20fill%3D%22%236b7280%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2000-1.414-1.414z%22%2F%3E%3c%2Fsvg%3E')] bg-[length:12px] bg-[right_12px_center] bg-no-repeat">{allPacks.filter(p_12 => p_12.category === item_3.category).map(_temp8)}</select></div><div><label htmlFor={`duration-${index_2}`} className="block text-base font-medium mb-2">{tf("duration")}</label><select id={`duration-${index_2}`} value={item_3.duration} onChange={e_1 => updateRentalItem(index_2, "duration", e_1.target.value)} className="w-full pl-4 pr-10 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3cpath%20fill%3D%22%236b7280%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2000-1.414-1.414z%22%2F%3E%3c%2Fsvg%3E')] bg-[length:12px] bg-[right_12px_center] bg-no-repeat"><option value="2h">2h</option><option value="halfday">{tf("halfDay")}</option><option value="1day">1 {tf("day")}</option></select></div></div>{item_3.category !== "other" && pack_4 && "separateItems" in pack_4 && pack_4.separateItems ? <div className="mb-4"><label htmlFor={`itemType-${index_2}`} className="block text-base font-medium mb-2">{tf("rentalType")}</label><select id={`itemType-${index_2}`} value={item_3.itemType || "complete"} onChange={e_2 => updateRentalItem(index_2, "itemType", e_2.target.value)} className="w-full pl-4 pr-10 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3cpath%20fill%3D%22%236b7280%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2000-1.414-1.414z%22%2F%3E%3c%2Fsvg%3E')] bg-[length:12px] bg-[right_12px_center] bg-no-repeat"><option value="complete">{tf("completePackBikeHelmet")}</option>{(pack_4.separateItems as Record<string, Record<string, number>>).bikeOnly && <option value="bikeOnly">{tf("bikeOnly")}</option>}{(pack_4.separateItems as Record<string, Record<string, number>>).helmetOnly && <option value="helmetOnly">{tf("helmetOnly")}</option>}</select></div> : null}{item_3.category === "other" && <div className="mb-4"><label htmlFor={`size-${index_2}`} className="block text-base font-medium mb-2">{tf("size")}</label><select id={`size-${index_2}`} value={item_3.accessorySize || "adult"} onChange={e_3 => updateRentalItem(index_2, "accessorySize", e_3.target.value)} className="w-full pl-4 pr-10 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3cpath%20fill%3D%22%236b7280%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2000-1.414-1.414z%22%2F%3E%3c%2Fsvg%3E')] bg-[length:12px] bg-[right_12px_center] bg-no-repeat"><option value="adult">{tf("adult")}</option><option value="junior">{tf("juniorChild")}</option></select></div>}<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4"><div><label htmlFor={`quantity-${index_2}`} className="block text-base font-medium mb-2">{tf("quantity")}</label><input type="number" id={`quantity-${index_2}`} min="1" value={item_3.quantity} onChange={e_4 => updateRentalItem(index_2, "quantity", parseInt(e_4.target.value) || 1)} className="w-full px-4 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background" /></div><div><p className="block text-base font-medium mb-2">{tf("insurance")}</p><label className="flex items-center gap-2 cursor-pointer px-4 py-3 border rounded-lg bg-background"><input type="checkbox" checked={item_3.withInsurance} onChange={e_5 => updateRentalItem(index_2, "withInsurance", e_5.target.checked)} className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary" /><span className="text-base">{tf("damage")} (+{rentalData.damageInsurance.price}€/{tf("day")})</span></label></div></div>{products.length > 0 && <div className="mt-4 p-4 bg-background rounded-lg border"><p className="text-base font-semibold mb-2">{pack_4?.name || tf("selectedPack")}</p><p className="text-sm text-muted-foreground mb-4">{tf("clickToSelectProducts")}</p><div className="relative"><div className="grid grid-cols-1 sm:grid-cols-3 gap-3">{(() => {
                const currentSlideIndex = carouselIndexes[index_2] || 0;
                const startIdx = currentSlideIndex * 3;
                const slideProducts = products.slice(startIdx, startIdx + 3);
                const selectedProducts = item_3.selectedProducts || [];
                return slideProducts.map((product, pIdx) => {
                  const productId = `${product.name}-${startIdx + pIdx}`;
                  const isSelected = selectedProducts.includes(productId);
                  return <button key={pIdx} type="button" onClick={() => {
                    const currentSelected = item_3.selectedProducts || [];
                    const newSelected = isSelected ? currentSelected.filter(id => id !== productId) : [...currentSelected, productId];
                    updateRentalItem(index_2, "selectedProducts", newSelected);
                  }} className={`text-left bg-muted/30 rounded-lg overflow-hidden transition-all ${isSelected ? "ring-2 ring-primary ring-offset-2" : "hover:bg-muted/50"}`}><div className="relative aspect-video"><Image src={product.photo} alt={product.name} fill={true} className="object-cover" sizes="(max-width: 640px) 100vw, 33vw" />{isSelected && <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center"><svg className="w-4 h-4 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg></div>}</div><div className="p-3"><p className="font-semibold text-sm text-foreground mb-1">{product.name}</p>{product.description && <p className="text-xs text-muted-foreground line-clamp-2">{product.description}</p>}</div></button>;
                });
              })()}</div>{products.length > 3 && <><button type="button" onClick={() => {
                const currentSlide = carouselIndexes[index_2] || 0;
                const totalSlides = Math.ceil(products.length / 3);
                const newSlide = currentSlide === 0 ? totalSlides - 1 : currentSlide - 1;
                setCarouselIndexes({
                  ...carouselIndexes,
                  [index_2]: newSlide
                });
              }} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors text-xl" aria-label="Previous">‹</button><button type="button" onClick={() => {
                const currentSlide_0 = carouselIndexes[index_2] || 0;
                const totalSlides_0 = Math.ceil(products.length / 3);
                const newSlide_0 = currentSlide_0 === totalSlides_0 - 1 ? 0 : currentSlide_0 + 1;
                setCarouselIndexes({
                  ...carouselIndexes,
                  [index_2]: newSlide_0
                });
              }} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors text-xl" aria-label="Next">›</button></>}{products.length > 3 && <div className="flex justify-center gap-1.5 mt-4">{Array.from({
                length: Math.ceil(products.length / 3)
              }).map((__0, slideIdx) => <button key={slideIdx} type="button" onClick={() => setCarouselIndexes({
                ...carouselIndexes,
                [index_2]: slideIdx
              })} className={`w-2 h-2 rounded-full transition-colors ${(carouselIndexes[index_2] || 0) === slideIdx ? "bg-primary" : "bg-muted-foreground/30 hover:bg-muted-foreground/50"}`} aria-label={`Go to slide ${slideIdx + 1}`} />)}</div>}</div></div>}</div>;
    });
    $[164] = allPacks;
    $[165] = carouselIndexes;
    $[166] = getPackProducts;
    $[167] = removeRentalItem;
    $[168] = rentalData.damageInsurance;
    $[169] = rentalItems;
    $[170] = tf;
    $[171] = updateRentalItem;
    $[172] = t82;
  } else {
    t82 = $[172];
  }
  let t83;
  if ($[173] !== t67 || $[174] !== t80 || $[175] !== t81 || $[176] !== t82) {
    t83 = <div>{t67}{t80}{t81}{t82}</div>;
    $[173] = t67;
    $[174] = t80;
    $[175] = t81;
    $[176] = t82;
    $[177] = t83;
  } else {
    t83 = $[177];
  }
  let t84;
  if ($[178] !== t) {
    t84 = t("message");
    $[178] = t;
    $[179] = t84;
  } else {
    t84 = $[179];
  }
  let t85;
  if ($[180] !== t84) {
    t85 = <label htmlFor="message" className="block text-base font-medium mb-2">{t84}</label>;
    $[180] = t84;
    $[181] = t85;
  } else {
    t85 = $[181];
  }
  let t86;
  if ($[182] !== tf) {
    t86 = tf("messagePlaceholderSummer");
    $[182] = tf;
    $[183] = t86;
  } else {
    t86 = $[183];
  }
  let t87;
  if ($[184] !== t86) {
    t87 = <textarea id="message" name="message" rows={3} placeholder={t86} className="w-full px-4 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background" />;
    $[184] = t86;
    $[185] = t87;
  } else {
    t87 = $[185];
  }
  let t88;
  if ($[186] !== t85 || $[187] !== t87) {
    t88 = <div>{t85}{t87}</div>;
    $[186] = t85;
    $[187] = t87;
    $[188] = t88;
  } else {
    t88 = $[188];
  }
  let t89;
  if ($[189] !== durationLabel || $[190] !== getPackName || $[191] !== getPackPrice || $[192] !== rentalData.damageInsurance || $[193] !== rentalItems || $[194] !== tf || $[195] !== totalPrice) {
    t89 = rentalItems.length > 0 && <div className="bg-muted/30 border rounded-xl p-6"><div className="flex justify-between items-center mb-5 pb-4 border-b"><h2 className="text-2xl font-bold text-foreground">{tf("summary")}</h2><div className="text-3xl font-bold text-foreground">{totalPrice}€</div></div><div className="space-y-3"><div className="text-base font-bold text-foreground mb-3">{tf("selectedItems")}</div>{rentalItems.map((item_4, index_3) => {
          const itemPrice_0 = getPackPrice(item_4);
          const insurancePrice = item_4.withInsurance ? rentalData.damageInsurance.price : 0;
          return <div key={index_3} className="bg-background border rounded-lg p-4"><div className="flex justify-between items-start mb-2"><div className="font-bold text-base text-foreground">{getPackName(item_4.packId, item_4.category)}<span className="text-muted-foreground font-medium ml-1">×{item_4.quantity}</span></div><span className="font-bold text-base text-foreground">{(itemPrice_0 + insurancePrice) * item_4.quantity}€</span></div><div className="flex gap-2 text-sm font-medium text-muted-foreground"><span>{durationLabel(item_4.duration)}</span>{item_4.withInsurance && <><span>•</span><span>{tf("withInsurance")}</span></>}</div></div>;
        })}</div></div>;
    $[189] = durationLabel;
    $[190] = getPackName;
    $[191] = getPackPrice;
    $[192] = rentalData.damageInsurance;
    $[193] = rentalItems;
    $[194] = tf;
    $[195] = totalPrice;
    $[196] = t89;
  } else {
    t89 = $[196];
  }
  const t90 = netlifyStatus === "submitting";
  let t91;
  if ($[197] !== locale || $[198] !== netlifyStatus || $[199] !== t) {
    t91 = netlifyStatus === "submitting" ? netlifyFormMessage(locale, "submitting") : t("submitButton");
    $[197] = locale;
    $[198] = netlifyStatus;
    $[199] = t;
    $[200] = t91;
  } else {
    t91 = $[200];
  }
  let t92;
  if ($[201] !== t90 || $[202] !== t91) {
    t92 = <button type="submit" disabled={t90} className="w-full bg-primary text-primary-foreground px-6 py-4 text-lg rounded-lg font-semibold hover:bg-primary/90 transition-colors shadow-md hover:shadow-lg disabled:opacity-60">{t91}</button>;
    $[201] = t90;
    $[202] = t91;
    $[203] = t92;
  } else {
    t92 = $[203];
  }
  let t93;
  if ($[204] !== locale || $[205] !== netlifyStatus) {
    t93 = <FormStatus status={netlifyStatus} locale={locale} />;
    $[204] = locale;
    $[205] = netlifyStatus;
    $[206] = t93;
  } else {
    t93 = $[206];
  }
  let t94;
  if ($[207] !== t83 || $[208] !== t88 || $[209] !== t89 || $[210] !== t92 || $[211] !== t93) {
    t94 = <div className="lg:col-span-8 space-y-6">{t83}{t88}{t89}{t92}{t93}</div>;
    $[207] = t83;
    $[208] = t88;
    $[209] = t89;
    $[210] = t92;
    $[211] = t93;
    $[212] = t94;
  } else {
    t94 = $[212];
  }
  return <div className="bg-card rounded-lg border p-4 sm:p-6 lg:p-8 w-full max-w-[1440px] mx-auto"><form name="contact-summer-rental" method="POST" data-netlify="true" netlify-honeypot="bot-field" onSubmit={handleNetlifySubmit} className="space-y-6">{t15}{t16}{t17}{t19}{t21}{t22}{t27}<div className="grid grid-cols-1 lg:grid-cols-12 gap-8"><div className="lg:col-span-4 space-y-4 lg:p-6 lg:bg-muted/30 lg:rounded-lg">{t31}{t35}{t39}{t43}<div>{t48}<DatePickerField name="pickupDate" id="pickupDate" locale={locale} required={true} minDate={new Date(today)} placeholder={locale === "fr" ? "Choisir une date" : "Pick a date"} /></div>{t56}{t65}</div>{t94}</div></form></div>;
}
function _temp8(pack_5) {
  return <option key={pack_5.id} value={pack_5.id}>{pack_5.name}</option>;
}
function _temp7(it) {
  return it.category !== "other";
}
function _temp6(p_10) {
  return p_10.category === "other";
}
function _temp5(p_9) {
  return p_9.category === "junior";
}
function _temp4(p_8) {
  return p_8.category === "adult";
}
function _temp3(p_1) {
  return {
    ...p_1,
    category: "other" as const
  };
}
function _temp2(p_0) {
  return {
    ...p_0,
    category: "junior" as const
  };
}
function _temp(p) {
  return {
    ...p,
    category: "adult" as const
  };
}